import styled, { DefaultTheme, CSSObject } from 'styled-components';
import * as Theme from '../../serviceTheme';
import { withConfig } from '../utils';

export interface InputStyleConfig {
    borderColorNormal: (theme: DefaultTheme) => string,
    backgroundColorNormal: (theme: DefaultTheme) => string,
    colorNormal: (theme: DefaultTheme) => string,

    borderColorNormalVoid: (theme: DefaultTheme) => string,
    backgroundColorNormalVoid: (theme: DefaultTheme) => string,
    colorNormalVoid: (theme: DefaultTheme) => string,

    borderColorDisabled: (theme: DefaultTheme) => string,
    backgroundColorDisabled: (theme: DefaultTheme) => string,
    colorDisabled: (theme: DefaultTheme) => string,

    borderColorDisabledVoid: (theme: DefaultTheme) => string,
    backgroundColorDisabledVoid: (theme: DefaultTheme) => string,
    colorDisabledVoid: (theme: DefaultTheme) => string,

    borderColorHover: (theme: DefaultTheme) => string,
    backgroundColorHover: (theme: DefaultTheme) => string,
    colorHover: (theme: DefaultTheme) => string,

    borderColorHoverVoid: (theme: DefaultTheme) => string,
    backgroundColorHoverVoid: (theme: DefaultTheme) => string,
    colorHoverVoid: (theme: DefaultTheme) => string,

    borderColorFocused: (theme: DefaultTheme) => string,
    backgroundColorFocused: (theme: DefaultTheme) => string,
    colorFocused: (theme: DefaultTheme) => string,
}

export type InputStyleConfigP = {
    cfg: InputStyleConfig,
    theme: DefaultTheme,
}

const G = {
    getColorNormal: ({ cfg, theme }: InputStyleConfigP) => cfg.colorNormal(theme),
    getBackgroundColorNormal: ({ cfg, theme }: InputStyleConfigP) => cfg.backgroundColorNormal(theme),
    getBorderColorNormal: ({ cfg, theme }: InputStyleConfigP) => cfg.borderColorNormal(theme),

    getColorNormalVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.colorNormalVoid(theme),
    getBackgroundColorNormalVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.backgroundColorNormalVoid(theme),
    getBorderColorNormalVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.borderColorNormalVoid(theme),

    getColorDisabled: ({ cfg, theme }: InputStyleConfigP) => cfg.colorDisabled(theme),
    getBackgroundColorDisabled: ({ cfg, theme }: InputStyleConfigP) => cfg.backgroundColorDisabled(theme),
    getBorderColorDisabled: ({ cfg, theme }: InputStyleConfigP) => cfg.borderColorDisabled(theme),

    getColorDisabledVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.colorDisabledVoid(theme),
    getBackgroundColorDisabledVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.backgroundColorDisabledVoid(theme),
    getBorderColorDisabledVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.borderColorDisabledVoid(theme),

    getColorHover: ({ cfg, theme }: InputStyleConfigP) => cfg.colorHover(theme),
    getBorderColorHover: ({ cfg, theme }: InputStyleConfigP) => cfg.borderColorHover(theme),
    getBackgroundColorHover: ({ cfg, theme }: InputStyleConfigP) => cfg.backgroundColorHover(theme),

    getColorHoverVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.colorHoverVoid(theme),
    getBorderColorHoverVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.borderColorHoverVoid(theme),
    getBackgroundColorHoverVoid: ({ cfg, theme }: InputStyleConfigP) => cfg.backgroundColorHoverVoid(theme),

    getColorFocused: ({ cfg, theme }: InputStyleConfigP) => cfg.colorFocused(theme),
    getBorderColorFocused: ({ cfg, theme }: InputStyleConfigP) => cfg.borderColorFocused(theme),
    getBackgroundColorFocused: ({ cfg, theme }: InputStyleConfigP) => cfg.backgroundColorFocused(theme),
}

export interface InputStyles {
    rest: (p: InputStyleConfigP) => CSSObject,
    normal: (p: InputStyleConfigP) => CSSObject,
    emptyValue: (p: InputStyleConfigP) => CSSObject,
    disabled: (p: InputStyleConfigP) => CSSObject,
    disabledEmptyValue: (p: InputStyleConfigP) => CSSObject,
    focus: (p: InputStyleConfigP) => CSSObject,
    hover: (p: InputStyleConfigP) => CSSObject,
    hoverEmptyValue: (p: InputStyleConfigP) => CSSObject,
}

export const InputColors: InputStyles = {
    rest: () => ({
        boxSizing: 'border-box',
        borderRadius: '3px',
        outline: '0',
        width: '100%',
    }),
    normal: ({ cfg, theme }) => ({
        backgroundColor: G.getBackgroundColorNormal({ cfg, theme }),
        border: `1px solid ${G.getBorderColorNormal({ cfg, theme })}`,
        color: G.getColorNormal({ cfg, theme }),
    }),
    emptyValue: ({ cfg, theme }) => ({
        backgroundColor: G.getBackgroundColorNormalVoid({ cfg, theme }),
        borderColor: G.getBorderColorNormalVoid({ cfg, theme }),
        color: G.getColorNormalVoid({ cfg, theme }),
    }),
    disabled: ({ cfg, theme }) => ({
        backgroundColor: G.getBackgroundColorDisabled({ cfg, theme }),
        borderColor: G.getBorderColorDisabled({ cfg, theme }),
        color: G.getColorDisabled({ cfg, theme }),
    }),
    disabledEmptyValue: ({ cfg, theme }) => ({
        backgroundColor: G.getBackgroundColorDisabledVoid({ cfg, theme }),
        borderColor: G.getBorderColorDisabledVoid({ cfg, theme }),
        color: G.getColorDisabledVoid({ cfg, theme }),
    }),
    focus: ({ cfg, theme }) => ({
        backgroundColor: G.getBackgroundColorFocused({ cfg, theme }),
        borderColor: G.getBorderColorFocused({ cfg, theme }),
        color: G.getColorFocused({ cfg, theme }),
    }),
    hover: ({ cfg, theme }) => ({
        backgroundColor: G.getBackgroundColorHover({ cfg, theme }),
        borderColor: G.getBorderColorHover({ cfg, theme }),
        color: G.getColorHover({ cfg, theme }),
    }),
    hoverEmptyValue: ({ cfg, theme }) => ({
        backgroundColor: G.getBackgroundColorHoverVoid({ cfg, theme }),
        borderColor: G.getBorderColorHoverVoid({ cfg, theme }),
        color: G.getColorHoverVoid({ cfg, theme }),
    }),
}

const Base = styled.input`
    padding: .5em .7em;

    ${InputColors.rest}

    ${InputColors.normal}

    &[value=""] {
        ${InputColors.emptyValue}
    }

    &:disabled {
        ${InputColors.disabled}

        &[value=""] {
            ${InputColors.disabledEmptyValue}
        }
    }

    &:focus {
        ${InputColors.focus}
    }

    &:not(:focus):not(:disabled):hover {
        ${InputColors.hover}

        &[value=""] {
            ${InputColors.hoverEmptyValue}
        }
    }

    &::placeholder {
        color: #898f9e;
    }
`;

export const InputConfig: InputStyleConfig = {

    /* Отключенное поле */
    borderColorDisabledVoid: () => 'transparent',
    backgroundColorDisabledVoid: () => '#D9D9D9',
    colorDisabledVoid: () => '#898F9E',

    /* Отключенное заполненное поле */
    borderColorDisabled: () => 'transparent',
    backgroundColorDisabled: () => '#D9D9D9',
    colorDisabled: () => '#000',

    /* Пустое поле */
    borderColorNormalVoid: () => '#c9ccd5',
    backgroundColorNormalVoid: () => '#fff',
    colorNormalVoid: () => '#898F9E',

    /* Пустое поле - наведение */
    borderColorHoverVoid: () => '#989EAE',
    backgroundColorHoverVoid: () => '#fff',
    colorHoverVoid: () => '#898F9E',

    /* Заполненное поле */
    borderColorNormal: () => '#c9ccd5',
    backgroundColorNormal: () => '#fff',
    colorNormal: () => '#000',

    /* Заполненное поле наведение */
    borderColorHover: () => '#A2A7B3',
    backgroundColorHover: () => '#fff',
    colorHover: () => '#000',

    /* Нажатие и заполнение поля */
    borderColorFocused: (t) => Theme.safeGetPrimary({ theme: t }),
    backgroundColorFocused: () => '#fff',
    colorFocused: () => '#000',

}

interface InputProps {
    name: string,
    disabled: boolean,
    value: string,
}

export const Input = withConfig<InputProps, HTMLInputElement>()(InputConfig)(Base);

export const INPUT_HEIGHT = '30px';
